// pages/AdministrationDashboard.js
// Dashboard Amministrazione V2 — SCRIVANIA OPERATIVA del ragioniere ("cosa devo fare adesso?").
// 4 KPI operativi (Da fatturare · Da incassare · Scaduto · Verifiche) + 2 work-list con azioni inline.
// Read-model canonico: GET /api/dashboard/amministrazione (riuso build_customer_receivables_view).
// Azioni: riuso ESCLUSIVO degli endpoint lifecycle esistenti (milestone PUT, extra/variation mark-paid).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { dashboardApi } from '../lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import {
  FileText, Banknote, ChevronRight, ChevronDown, ChevronUp, Inbox,
  AlertTriangle, ShieldCheck, CheckCircle, Bell,
} from 'lucide-react';
import { formatCurrency } from '../lib/utils';

const PAGE = 5;
const today = () => new Date().toISOString().split('T')[0];

// ---- Azioni: mapping sugli endpoint lifecycle ESISTENTI (nessuna nuova business logic) ----
const invoiceReq = (row, form) => ({
  url: `/api/projects/${row.project_id}/milestones/${row.milestone_id}`,
  method: 'PUT',
  body: { status: 'fatturato', invoice_number: form.invoice_number.trim(), invoice_date: form.invoice_date, due_date: form.due_date || null },
});
const collectReq = (row, form) => {
  if (row.item_type === 'extra') return { url: `/api/extra-forniture-payments/${row.milestone_id}/mark-paid`, method: 'POST', body: { payment_date: form.payment_date } };
  if (row.item_type === 'variazione') return { url: `/api/variation-payments/${row.milestone_id}/mark-paid`, method: 'POST', body: { payment_date: form.payment_date } };
  return { url: `/api/projects/${row.project_id}/milestones/${row.milestone_id}`, method: 'PUT', body: { status: 'incassato', payment_date: form.payment_date } };
};
const noticeUrl = (row) => {
  if (row.item_type === 'extra') return `/api/extra-forniture-payments/${row.milestone_id}/invoice-notice`;
  if (row.item_type === 'variazione') return null; // le variazioni non hanno avviso PDF
  return `/api/projects/${row.project_id}/milestones/${row.milestone_id}/invoice-notice`;
};
const extractError = async (res) => {
  try { const j = await res.json(); return j.detail || j.message || `Errore ${res.status}`; }
  catch { return `Errore ${res.status}`; }
};

// ---- KPI compatto (4-up, stessa altezza) ----
const KpiCard = ({ id, title, value, mode = 'money', microcopy, tone = 'orange', icon: Icon, active, onClick, tooltip }) => {
  const toneCls = {
    orange: { icon: 'text-[#FF7819]', iconBg: 'bg-orange-50', ring: 'border-[#FF7819] ring-1 ring-[#FF7819]' },
    rose: { icon: 'text-rose-600', iconBg: 'bg-rose-50', ring: 'border-rose-400 ring-1 ring-rose-300' },
    slate: { icon: 'text-slate-500', iconBg: 'bg-slate-100', ring: 'border-slate-400 ring-1 ring-slate-300' },
  }[tone] || {};
  const Wrap = onClick ? 'button' : 'div';
  return (
    <Wrap onClick={onClick} data-testid={`admin-kpi-${id}`} title={tooltip || undefined}
      className={`text-left bg-white rounded-xl border p-4 transition-all shadow-sm ${onClick ? 'hover:shadow-md' : ''} ${
        active ? toneCls.ring : 'border-[#E4E4E4]'}`}>
      <div className="flex items-center justify-between">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-400">{title}</p>
        <div className={`w-8 h-8 rounded-lg ${toneCls.iconBg} flex items-center justify-center`}>
          <Icon className={`w-4 h-4 ${toneCls.icon}`} />
        </div>
      </div>
      <p className="text-2xl font-bold text-[#3C3C3C] mt-2 tabular-nums" data-testid={`admin-kpi-${id}-value`}>
        {mode === 'money' ? formatCurrency(value) : value}
      </p>
      <p className="text-[11px] text-gray-500 mt-1 leading-tight truncate" data-testid={`admin-kpi-${id}-microcopy`}>{microcopy}</p>
    </Wrap>
  );
};

// ---- Riga work-desk con azioni inline ----
const DeskRow = ({ row, kind, onInvoice, onCollect }) => {
  const ageText = row.age_days == null ? '' : (row.age_days === 0 ? 'oggi' : `${row.age_days} ${row.age_days === 1 ? 'giorno' : 'giorni'}`);
  const urgency = kind === 'fatture'
    ? (ageText ? `Sbloccato da ${ageText}` : '')
    : (ageText ? `Fatturato da ${ageText}` : '');
  const urgent = row.age_days != null && row.age_days > 7;
  const notice = noticeUrl(row);
  const downloadNotice = async () => {
    if (!notice) return;
    try {
      const res = await fetch(notice, { credentials: 'include' });
      if (!res.ok) { toast.error(await extractError(res)); return; }
      const blob = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `avviso_${row.project_number || 'doc'}_${row.item_type || 'milestone'}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(a.href);
    } catch { toast.error('Errore download avviso'); }
  };
  const typeBadge = row.item_type === 'extra'
    ? { cls: 'bg-[#ECEBFB] text-[#5B54D6]', label: 'Extra' }
    : row.item_type === 'variazione'
    ? { cls: 'bg-[#E6F6F1] text-[#1E9E5A]', label: 'Variazione' }
    : { cls: 'bg-[#FFF1E6] text-[#FF7819]', label: 'Milestone' };
  return (
    <div className="p-3 rounded-lg hover:bg-gray-50 transition-colors group" data-testid={`admin-row-${row.milestone_id}`}>
      <div className="flex items-start justify-between gap-3">
        <Link to={row.link || '#'} className="min-w-0 no-underline text-inherit flex-1">
          <p className="text-sm font-medium text-gray-900 truncate flex items-center gap-1.5">
            <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide shrink-0 ${typeBadge.cls}`}
              data-testid={`admin-row-${row.milestone_id}-type`}>{typeBadge.label}</span>
            <span className="truncate">{row.client_name}</span>
            {row.project_number ? <span className="text-gray-400 font-normal truncate"> · {row.project_number}</span> : null}
          </p>
          <p className="text-xs text-gray-500 truncate">
            <span className="font-medium">{row.milestone_name}</span>
            {row.site_address ? <span className="text-gray-400"> · {row.site_address}</span> : null}
          </p>
          <p className="text-[11px] mt-0.5 leading-tight" data-testid={`admin-row-${row.milestone_id}-age`}>
            {urgency ? <span className={urgent ? 'font-semibold text-rose-600' : 'font-medium text-gray-600'}>{urgency}{urgent ? ' · sollecitare' : ''}</span> : <span className="text-gray-400">{row.status_label}</span>}
            {row.pm_name ? <span className="text-gray-400"> · PM {row.pm_name}</span> : null}
          </p>
        </Link>
        <div className="text-right shrink-0">
          <span className="text-sm font-semibold text-[#3C3C3C] whitespace-nowrap tabular-nums">{formatCurrency(row.amount)}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 mt-2">
        {kind === 'fatture' ? (
          <Button size="sm" className="h-7 text-xs bg-[#FF7819] hover:bg-[#e06a15]" onClick={() => onInvoice(row)} data-testid={`admin-mark-invoiced-${row.milestone_id}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-1" />Segna fatturato
          </Button>
        ) : (
          <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700" onClick={() => onCollect(row)} data-testid={`admin-mark-collected-${row.milestone_id}`}>
            <CheckCircle className="w-3.5 h-3.5 mr-1" />Segna incassato
          </Button>
        )}
        {notice && (
          <Button size="sm" variant="outline" className="h-7 text-xs" onClick={downloadNotice} data-testid={`admin-avviso-${row.milestone_id}`}>
            <Bell className="w-3.5 h-3.5 mr-1" />Avviso
          </Button>
        )}
      </div>
    </div>
  );
};

const DeskList = ({ title, icon: Icon, rows, emptyText, testId, kind, filterable, onInvoice, onCollect }) => {
  const [visible, setVisible] = useState(PAGE);
  const [typeFilter, setTypeFilter] = useState('tutti');
  const msCount = rows.filter((r) => r.item_type === 'milestone' || !r.item_type).length;
  const exCount = rows.filter((r) => r.item_type === 'extra').length;
  const varCount = rows.filter((r) => r.item_type === 'variazione').length;
  const _match = { milestone: (r) => r.item_type === 'milestone' || !r.item_type, extra: (r) => r.item_type === 'extra', variazioni: (r) => r.item_type === 'variazione' };
  const filtered = filterable && typeFilter !== 'tutti' ? rows.filter(_match[typeFilter]) : rows;
  const shown = filtered.slice(0, visible);
  const chips = [
    { key: 'tutti', label: 'Tutti', count: rows.length },
    { key: 'milestone', label: 'Milestone', count: msCount },
    { key: 'extra', label: 'Extra', count: exCount },
    { key: 'variazioni', label: 'Variazioni', count: varCount },
  ];
  return (
    <Card className="bg-white rounded-xl border border-[#E4E4E4] shadow-sm" data-testid={testId}>
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center justify-between">
          <span className="flex items-center gap-2"><Icon className="w-5 h-5 text-[#FF7819]" />{title}</span>
          <span className="text-sm font-normal text-gray-400">{filtered.length}</span>
        </CardTitle>
        {filterable && (
          <div className="flex items-center gap-1.5 flex-wrap pt-1" data-testid={`${testId}-filters`}>
            {chips.map((c) => (
              <button key={c.key} onClick={() => { setTypeFilter(c.key); setVisible(PAGE); }}
                data-testid={`${testId}-filter-${c.key}`}
                className={`px-2.5 py-1 text-xs rounded-full border transition-colors ${
                  typeFilter === c.key ? 'bg-[#FF7819] text-white border-[#FF7819]' : 'bg-white text-[#525252] border-slate-300 hover:border-[#FF7819]'}`}>
                {c.label} <span className="opacity-70">{c.count}</span>
              </button>
            ))}
          </div>
        )}
      </CardHeader>
      <CardContent className="pt-0">
        {filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid={`${testId}-empty`}>
            <div className="w-12 h-12 bg-gray-50 rounded-full flex items-center justify-center mb-2"><Inbox className="w-6 h-6 text-gray-400" /></div>
            <p className="text-sm font-medium text-gray-600">{emptyText}</p>
          </div>
        ) : (
          <>
            <div className="space-y-0.5" data-testid={`${testId}-list`}>
              {shown.map((row) => <DeskRow key={row.milestone_id} row={row} kind={kind} onInvoice={onInvoice} onCollect={onCollect} />)}
            </div>
            {filtered.length > PAGE && (
              <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
                <span className="text-xs text-gray-500" data-testid={`${testId}-count`}>{Math.min(visible, filtered.length)} di {filtered.length}</span>
                <div className="flex items-center gap-3">
                  {visible < filtered.length && (
                    <button onClick={() => setVisible((v) => v + PAGE)} data-testid={`${testId}-more`} className="text-xs font-medium text-[#FF7819] hover:underline flex items-center gap-1">
                      Vedi tutte <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  )}
                  {visible > PAGE && (
                    <button onClick={() => setVisible(PAGE)} data-testid={`${testId}-less`} className="text-xs font-medium text-gray-500 hover:underline flex items-center gap-1">
                      Riduci <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

const AdministrationDashboard = () => {
  const { isAdmin, isAdministration } = useAuth();
  const canAct = isAdmin || isAdministration;
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [focus, setFocus] = useState(null);
  const [dialog, setDialog] = useState(null); // { type:'invoice'|'collect', row }
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const fattRef = useRef(null);
  const incRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await dashboardApi.getAmministrazione();
      setData(res.data);
    } catch (e) {
      console.error('Errore dashboard amministrazione:', e);
      toast.error('Errore nel caricamento della dashboard');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openInvoice = (row) => { setForm({ invoice_number: '', invoice_date: today(), due_date: '' }); setDialog({ type: 'invoice', row }); };
  const openCollect = (row) => { setForm({ payment_date: today() }); setDialog({ type: 'collect', row }); };

  const confirmAction = async () => {
    const { type, row } = dialog;
    if (type === 'invoice' && !form.invoice_number?.trim()) { toast.error('Numero fattura obbligatorio'); return; }
    if (type === 'collect' && !form.payment_date) { toast.error('Data incasso obbligatoria'); return; }
    setSaving(true);
    try {
      const { url, method, body } = type === 'invoice' ? invoiceReq(row, form) : collectReq(row, form);
      const res = await fetch(url, { method, credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      if (!res.ok) { toast.error(await extractError(res)); return; }
      toast.success(type === 'invoice' ? 'Voce segnata come fatturata' : 'Incasso registrato');
      setDialog(null);
      await load();
    } catch { toast.error('Errore di rete'); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin w-8 h-8 border-4 border-[#FF7819] border-t-transparent rounded-full" /></div>;
  }
  if (!data) {
    return <div className="flex items-center justify-center h-64"><p className="text-gray-500">Errore nel caricamento della dashboard</p></div>;
  }

  const {
    da_fatturare_count = 0, da_fatturare_value = 0,
    da_incassare_count = 0, da_incassare_value = 0,
    da_incassare_milestone_count = 0, da_incassare_extra_count = 0, da_incassare_variazioni_count = 0,
    da_fatturare_oldest_days = null, da_incassare_oldest_days = null,
    scaduto_value = 0, scaduto_count = 0,
    verifiche_count = 0, verifiche_iva_mancante = 0, verifiche_non_associato = 0,
    fatture = [], incassi = [],
  } = data;

  const incBreakdown = [`${da_incassare_milestone_count} milestone`]
    .concat(da_incassare_extra_count ? [`${da_incassare_extra_count} extra`] : [])
    .concat(da_incassare_variazioni_count ? [`${da_incassare_variazioni_count} variazione`] : [])
    .join(' · ');

  const focusList = (key, ref) => { setFocus((cur) => (cur === key ? null : key)); ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); };

  return (
    <div className="space-y-5 animate-fade-in" data-testid="amministrazione-dashboard">
      {/* HEADER compatto (§3) */}
      <div>
        <h1 className="text-xl font-bold text-gray-900">Amministrazione</h1>
        <p className="text-sm text-gray-500 mt-0.5">Fatture da emettere e crediti cliente da incassare</p>
      </div>

      {/* TOP CONTROL STRIP — 4 KPI (§4-§8) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3" data-testid="admin-kpi-grid">
        <KpiCard id="da_fatturare" title="Da fatturare" value={da_fatturare_value} icon={FileText} tone="orange"
          microcopy={`${da_fatturare_count} ${da_fatturare_count === 1 ? 'voce' : 'voci'} da emettere${da_fatturare_oldest_days != null ? ` · ${da_fatturare_oldest_days} gg` : ''}`}
          active={focus === 'fatture'} onClick={() => focusList('fatture', fattRef)} />
        <KpiCard id="da_incassare" title="Da incassare" value={da_incassare_value} icon={Banknote} tone="orange"
          microcopy={`${da_incassare_count} crediti${incBreakdown ? ` · ${incBreakdown}` : ''}`}
          active={focus === 'incassi'} onClick={() => focusList('incassi', incRef)} />
        <KpiCard id="scaduto" title="Scaduto" value={scaduto_value} icon={AlertTriangle} tone={scaduto_count > 0 ? 'rose' : 'slate'}
          tooltip="Solo voci con scadenza disponibile (esclude i Lavori senza scadenza fattura)"
          microcopy={scaduto_count > 0 ? `${scaduto_count} ${scaduto_count === 1 ? 'posizione' : 'posizioni'} · solo voci con scadenza` : 'Nessun credito scaduto · solo voci con scadenza'} />
        <KpiCard id="verifiche" title="Verifiche" value={verifiche_count} mode="count"
          icon={verifiche_count > 0 ? AlertTriangle : ShieldCheck} tone={verifiche_count > 0 ? 'rose' : 'slate'}
          microcopy={verifiche_count > 0 ? `${verifiche_iva_mancante} IVA mancante · ${verifiche_non_associato} non associati` : 'Nessuna verifica aperta'} />
      </div>

      {/* WORK DESK — 2 colonne (§9) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
        <div ref={fattRef} className={focus === 'incassi' ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
          <DeskList title="Fatture da emettere" icon={FileText} rows={fatture} kind="fatture"
            emptyText="Nessuna milestone da fatturare" testId="admin-fatture"
            onInvoice={canAct ? openInvoice : () => toast.error('Permesso negato')} onCollect={openCollect} />
        </div>
        <div ref={incRef} className={focus === 'fatture' ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
          <DeskList title="Incassi da ricevere" icon={Banknote} rows={incassi} kind="incassi" filterable
            emptyText="Nessun incasso da ricevere" testId="admin-incassi"
            onInvoice={openInvoice} onCollect={canAct ? openCollect : () => toast.error('Permesso negato')} />
        </div>
      </div>

      {/* VERIFICHE STRIP compatta (§15-§16) */}
      <div className="rounded-xl border border-[#E4E4E4] bg-white px-4 py-3 flex items-center gap-4 flex-wrap text-sm" data-testid="admin-verifiche-strip">
        <span className="font-semibold text-gray-700 flex items-center gap-1.5">
          {verifiche_count > 0 ? <AlertTriangle className="w-4 h-4 text-rose-500" /> : <ShieldCheck className="w-4 h-4 text-emerald-500" />}
          Verifiche amministrative
        </span>
        {verifiche_count > 0 ? (
          <>
            <span className="text-gray-600" data-testid="verifiche-iva">{verifiche_iva_mancante} IVA mancante</span>
            <span className="text-gray-600" data-testid="verifiche-non-associato">{verifiche_non_associato} {verifiche_non_associato === 1 ? 'progetto non associato' : 'progetti non associati'}</span>
          </>
        ) : (
          <span className="text-emerald-600" data-testid="verifiche-ok">Nessuna verifica amministrativa</span>
        )}
      </div>

      {/* DIALOG azione */}
      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent data-testid="admin-action-dialog">
          <DialogHeader>
            <DialogTitle>{dialog?.type === 'invoice' ? 'Segna fatturato' : 'Segna incassato'} — {dialog?.row?.project_number || dialog?.row?.client_name}</DialogTitle>
          </DialogHeader>
          {dialog?.type === 'invoice' ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Numero fattura *</Label>
                <Input value={form.invoice_number || ''} onChange={(e) => setForm((p) => ({ ...p, invoice_number: e.target.value }))} data-testid="admin-invoice-number" placeholder="es. 123/2026" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Data fattura *</Label>
                  <Input type="date" value={form.invoice_date || ''} onChange={(e) => setForm((p) => ({ ...p, invoice_date: e.target.value }))} data-testid="admin-invoice-date" />
                </div>
                <div>
                  <Label className="text-xs">Scadenza (opzionale)</Label>
                  <Input type="date" value={form.due_date || ''} onChange={(e) => setForm((p) => ({ ...p, due_date: e.target.value }))} data-testid="admin-invoice-due" />
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Data incasso *</Label>
                <Input type="date" value={form.payment_date || ''} onChange={(e) => setForm((p) => ({ ...p, payment_date: e.target.value }))} data-testid="admin-payment-date" />
              </div>
            </div>
          )}
          {dialog?.row && (
            <div className="text-xs text-[#737373] bg-slate-50 rounded p-2">
              {dialog.row.milestone_name} · <span className="font-semibold">{formatCurrency(dialog.row.amount)}</span>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(null)} disabled={saving}>Annulla</Button>
            <Button className="bg-[#FF7819] hover:bg-[#e06a15]" onClick={confirmAction} disabled={saving} data-testid="admin-action-confirm">
              {saving ? 'Salvataggio...' : 'Conferma'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdministrationDashboard;
